import { useEffect, useState } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import { KeyRound, ShieldOff, Copy } from "lucide-react";

/**
 * TempAccessPanel — configures the single, shared temp-viewer account.
 * Backend: GET/PUT /api/admin/temp-access (admin only).
 *
 * A temp viewer is a preview-only user role. It sees ONLY products with the
 * `temp_visible` toggle flipped on; cart / checkout / orders / account are
 * blocked. The single credential is admin-rotated as needed.
 */
export default function TempAccessPanel() {
  const [state, setState] = useState(null); // { configured, enabled, username, ... }
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [enabled, setEnabled] = useState(true);
  const [verifiedGate, setVerifiedGate] = useState(true);
  const [complianceGate, setComplianceGate] = useState(true);
  const [busy, setBusy] = useState(false);

  const load = async () => {
    try {
      const { data } = await api.get("/admin/temp-access");
      setState(data);
      setUsername(data.username || "");
      // Once configured, mirror the backend flags. Before first setup, leave
      // the recommended defaults intact (both skips checked) so the admin
      // doesn't unwittingly force gates the temp user can't pass.
      if (data.configured) {
        setEnabled(data.enabled ?? true);
        setVerifiedGate(!!data.email_verified);
        setComplianceGate(!!data.compliance_acknowledged);
      }
    } catch (e) {
      toast.error(formatApiErrorDetail(e) || "Couldn't load temp access settings");
    }
  };

  useEffect(() => { load(); }, []);

  const save = async (e) => {
    e?.preventDefault?.();
    if (!username.trim()) { toast.error("Username required"); return; }
    if (!state?.configured && !password) { toast.error("Password required on first setup"); return; }
    setBusy(true);
    try {
      const payload = {
        username: username.trim(),
        enabled,
        email_verified: verifiedGate,
        compliance_acknowledged: complianceGate,
      };
      if (password) payload.password = password;
      const { data } = await api.put("/admin/temp-access", payload);
      setState(data);
      setPassword("");
      toast.success("Temp access saved.");
    } catch (e) {
      toast.error(formatApiErrorDetail(e) || "Couldn't save temp access.");
    } finally {
      setBusy(false);
    }
  };

  const copyUsername = async () => {
    if (!state?.username) return;
    try { await navigator.clipboard.writeText(state.username); toast.success("Username copied."); }
    catch { toast.error("Copy failed."); }
  };

  return (
    <div className="max-w-2xl mb-12" data-testid="temp-access-section">
      <h3 className="font-serif text-2xl mb-2">Temporary access</h3>
      <p className="text-sm text-charcoal/75 leading-relaxed mb-6 max-w-lg">
        One shared preview login. When enabled, this account can only browse products where you've flipped the <span className="font-mono">Temp: ON</span> toggle on the Products tab. Cart, checkout, orders, and account are blocked. Rotate the credential any time — old sessions expire on the next request.
      </p>

      <form onSubmit={save} className="border border-borderSubtle bg-white p-6 rounded-2xl space-y-5" data-testid="temp-access-form">
        <div className="grid sm:grid-cols-2 gap-5">
          <div>
            <label className="overline font-bold text-charcoal">Username</label>
            <input
              type="text"
              value={username}
              onChange={(e) => setUsername(e.target.value)}
              placeholder="BBKTEMP"
              autoCapitalize="off"
              autoCorrect="off"
              spellCheck={false}
              maxLength={32}
              pattern="[A-Za-z0-9._\-]{3,32}"
              className="mt-2 w-full py-2 px-3 border border-borderSubtle bg-white focus-visible:outline-none focus-visible:border-oxblood rounded font-mono text-sm"
              data-testid="temp-access-username-input"
              required
            />
            <p className="text-[12px] text-charcoal/75 mt-1.5">Case-sensitive · 3-32 chars · letters, digits, and <span className="font-mono">.</span> <span className="font-mono">_</span> <span className="font-mono">-</span></p>
          </div>
          <div>
            <label className="overline font-bold text-charcoal">Password {state?.configured && <span className="text-charcoal/75 normal-case tracking-normal">(leave blank to keep)</span>}</label>
            <div className="relative mt-2">
              <KeyRound size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-charcoal/75" />
              <input
                type="text"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder={state?.configured ? "•••••••• (unchanged)" : "Set a new password (min 6)"}
                autoCapitalize="off"
                autoCorrect="off"
                spellCheck={false}
                minLength={state?.configured ? 0 : 6}
                maxLength={128}
                className="pl-9 w-full py-2 pr-3 border border-borderSubtle bg-white focus-visible:outline-none focus-visible:border-oxblood rounded font-mono text-sm"
                data-testid="temp-access-password-input"
              />
            </div>
            <p className="text-[12px] text-charcoal/75 mt-1.5">Shown once — write it down before you save. Min 6 characters.</p>
          </div>
        </div>

        <div className="flex items-center gap-3 pt-1">
          <button
            type="button"
            onClick={() => setEnabled((v) => !v)}
            className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${enabled ? "bg-charcoal" : "bg-oxblood"}`}
            data-testid="temp-access-enabled-toggle"
            aria-pressed={enabled}
          >
            <span className={`inline-block h-4 w-4 transform rounded-full bg-bone transition-transform ${enabled ? "translate-x-6" : "translate-x-1"}`} />
          </button>
          <div>
            <p className="text-sm font-medium">{enabled ? "Login is active" : "Login is disabled"}</p>
            <p className="text-[13px] text-charcoal/75">
              {enabled ? "The credentials above will authenticate." : "The credentials will be rejected until re-enabled."}
            </p>
          </div>
        </div>

        <div className="pt-1 space-y-3 border-t border-borderSubtle pt-4">
          <p className="overline text-charcoal">Gate bypass</p>
          <label className="flex items-start gap-3 cursor-pointer">
            <input
              type="checkbox"
              checked={verifiedGate}
              onChange={(e) => setVerifiedGate(e.target.checked)}
              className="mt-1 accent-oxblood"
              data-testid="temp-access-verified-checkbox"
            />
            <div>
              <p className="text-sm">Skip email verification gate</p>
              <p className="text-[13px] text-charcoal/75">Recommended — temp email is a system placeholder and can't receive real links.</p>
            </div>
          </label>
          <label className="flex items-start gap-3 cursor-pointer">
            <input
              type="checkbox"
              checked={complianceGate}
              onChange={(e) => setComplianceGate(e.target.checked)}
              className="mt-1 accent-oxblood"
              data-testid="temp-access-compliance-checkbox"
            />
            <div>
              <p className="text-sm">Skip compliance acknowledgment gate</p>
              <p className="text-[13px] text-charcoal/75">Uncheck to make the temp visitor sign the research-use terms on first login.</p>
            </div>
          </label>
        </div>

        <div className="pt-3 border-t border-borderSubtle flex flex-wrap items-center gap-3">
          <button
            type="submit"
            disabled={busy}
            className="px-6 py-2.5 bg-charcoal hover:bg-oxblood text-bone tracking-[0.22em] text-[13px] uppercase rounded-full transition-colors disabled:opacity-50"
            data-testid="temp-access-save-btn"
          >
            {busy ? "Saving…" : state?.configured ? "Update temp access" : "Create temp access"}
          </button>
          {state?.configured && (
            <button
              type="button"
              onClick={copyUsername}
              className="inline-flex items-center gap-2 px-4 py-2.5 border border-borderSubtle text-charcoal/85 rounded-full text-[13px] uppercase tracking-[0.22em] hover:border-charcoal hover:text-charcoal transition-colors"
              data-testid="temp-access-copy-username"
            >
              <Copy size={12} /> Copy username
            </button>
          )}
          {state?.enabled ? (
            <span className="inline-flex items-center gap-1.5 text-[12px] font-mono uppercase tracking-widest text-emerald-700 bg-emerald-50 border border-emerald-200 px-2.5 py-1 rounded-full" data-testid="temp-access-status-on">
              <span className="w-1.5 h-1.5 rounded-full bg-emerald-600" /> Live
            </span>
          ) : (
            <span className="inline-flex items-center gap-1.5 text-[12px] font-mono uppercase tracking-widest text-oxblood bg-oxblood/10 border border-oxblood/30 px-2.5 py-1 rounded-full" data-testid="temp-access-status-off">
              <ShieldOff size={11} /> Off
            </span>
          )}
        </div>
      </form>
    </div>
  );
}
